import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from 'sonner';
import Navbar from './components/Layout/Navbar';
import Footer from './components/Layout/Footer';
import DynamicPageTitle from './components/DynamicPageTitle';
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import VerifyEmail from './pages/VerifyEmail';
import VerifyEmailPending from './pages/VerifyEmailPending';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import PaymentLink from './pages/PaymentLink';
import PaymentSuccess from './pages/PaymentSuccess';
import ReferralLanding from './pages/ReferralLanding';
import Offer from './pages/Offer';
import FAQ from './pages/FAQ';
import Tutorials from './pages/Tutorials';
import Assistance from './pages/Assistance';
import AssistantAdmin from './pages/Admin/AssistantAdmin';
import BecomeVendor from './pages/BecomeVendor';
import InfluencerProgram from './pages/InfluencerProgram';
import DownloadCenter from './pages/DownloadCenter';
import MentionsLegales from './pages/MentionsLegales';
import TermsClients from './pages/TermsClients';
import TermsVendors from './pages/TermsVendors';
import Privacy from './pages/Privacy';
import ClientDashboard from './pages/Client/ClientDashboard';
import ClientCreditsHistory from './pages/Client/ClientCreditsHistory';
import ClientTickets from './pages/Client/ClientTickets';
import AdminLogin from './pages/Admin/AdminLogin';
import AdminDashboard from './pages/Admin/AdminDashboard';
import AdminSettings from './pages/Admin/AdminSettings';
import Analytics from './pages/Admin/Analytics';
import ClientsManagement from './pages/Admin/ClientsManagement';
import VendorsManagement from './pages/Admin/VendorsManagement';
import PayoutsManagement from './pages/Admin/PayoutsManagement';
import PendingValidations from './pages/Admin/PendingValidations';
import FraudPage from './pages/Admin/FraudPage';
import IncidentModalPage from './pages/Admin/IncidentModal';
import ReviewsManagement from './pages/Admin/ReviewsManagement';
import EmailsPage from './pages/Admin/EmailsPage';
import EmailLogsPage from './pages/Admin/EmailLogsPage';
import AdminTickets from './pages/Admin/AdminTickets';
import AdminInfluencers from './pages/Admin/AdminInfluencers';
import RenewalsManagement from './pages/Admin/RenewalsManagement';
import AdminLayout from './components/Admin/AdminLayout';
import VendorDashboard from './pages/Vendor/VendorDashboard';
import Academy from './pages/Vendor/Academy';
import VendorStatistics from './pages/Vendor/VendorStatistics';
import VendorAnalytics from './pages/Vendor/VendorAnalytics';
import VendorTransactions from './pages/Vendor/VendorTransactions';
import VendorTickets from './pages/Vendor/VendorTickets';
import AccountSettings from './pages/AccountSettings';
import ChangePassword from './pages/ChangePassword';
import VerifyEmailChange from './pages/VerifyEmailChange';
import IncidentModal from './components/Incident/IncidentModal';
import InfluencerLayout from './components/Influencer/InfluencerLayout';
import InfluencerDashboard from './pages/Influencer/InfluencerDashboard';
import InfluencerStats from './pages/Influencer/InfluencerStats';
import InfluencerProfile from './pages/Influencer/InfluencerProfile';
import InfluencerPayouts from './pages/Influencer/InfluencerPayouts';
import InfluencerRegister from './pages/Influencer/InfluencerRegister';
import MaintenancePage from './pages/MaintenancePage';
import MaintenanceExit from './pages/MaintenanceExit';
import PaymentLayout from './components/Payment/PaymentLayout';
import PaymentHome from './pages/Payment/PaymentHome';
import PaymentProduct from './pages/Payment/PaymentProduct';
import PaymentCheckout from './pages/Payment/PaymentCheckout';
import PaymentSuccessPage from './pages/Payment/PaymentSuccess';
import PaymentCancel from './pages/Payment/PaymentCancel';
import PaymentCGV from './pages/Payment/PaymentCGV';
import PaymentMentions from './pages/Payment/PaymentMentions';
import PaymentPrivacy from './pages/Payment/PaymentPrivacy';
import GTMProvider from './components/GTMProvider';
import NovaFloatingWidget from './components/Assistant/NovaFloatingWidget';
import api from './services/api';
import './App.css';

const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-[#00FFFF] text-xl">Chargement...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/connexion" replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Maintenance check wrapper
const MaintenanceCheck = ({ children }) => {
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const { user } = useAuth();
  
  // Routes qui sont toujours accessibles (admin + page exit maintenance)
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isMaintenancePage = location.pathname === '/maintenance';
  const isMaintenanceExit = location.pathname === '/maintenance/exit';
  
  useEffect(() => {
    const checkMaintenance = async () => {
      try {
        const response = await api.get('/maintenance/status');
        setIsMaintenanceMode(response.data.maintenance_mode);
      } catch (err) {
        // Si erreur, on considère que le site est en ligne
        setIsMaintenanceMode(false);
      } finally {
        setLoading(false);
      }
    };
    
    checkMaintenance();
    // Vérifier périodiquement (toutes les 30 secondes)
    const interval = setInterval(checkMaintenance, 30000);
    return () => clearInterval(interval);
  }, []);
  
  // Pendant le chargement, on affiche rien
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center">
        <div className="text-[#FF6B35] text-xl">Chargement...</div>
      </div>
    );
  }
  
  // Si maintenance activée et pas admin et pas sur la page maintenance/exit
  if (isMaintenanceMode && !isAdminRoute && !isMaintenancePage && !isMaintenanceExit && user?.role !== 'admin') {
    return <Navigate to="/maintenance" replace />;
  }
  
  return children;
};

// Hook pour détecter si on est sur Site B (domaine de paiement)
const useSiteDetection = () => {
  const [siteMode, setSiteMode] = useState(null); // null = loading, 'site_a', 'site_b', 'unified'

  useEffect(() => {
    const detect = async () => {
      try {
        const res = await api.get('/payment/config');
        const siteBHostname = res.data.site_b_hostname || '';
        
        if (!siteBHostname) {
          // Pas de domaine Site B configuré → mode unifié (pré-fork)
          setSiteMode('unified');
        } else if (window.location.hostname.toLowerCase() === siteBHostname.toLowerCase()) {
          setSiteMode('site_b');
        } else {
          setSiteMode('site_a');
        }
      } catch {
        // Si erreur, mode unifié par défaut
        setSiteMode('unified');
      }
    };
    detect();
  }, []);

  return siteMode;
};

// Page 403 pour les routes bloquées
const BlockedRoute = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#0A0A0B]">
    <div className="text-center">
      <h1 className="text-2xl font-bold text-white mb-2">{"Page non disponible"}</h1>
      <p className="text-[#B8B8BA]">{"Cette page n\u2019est pas accessible depuis ce domaine."}</p>
    </div>
  </div>
);

const AppRoutes = () => {
  const location = useLocation();
  const siteMode = useSiteDetection();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isMaintenancePage = location.pathname === '/maintenance' || location.pathname === '/maintenance/exit';
  const isPaymentRoute = location.pathname.startsWith('/payment');
  const isInfluencerRoute = location.pathname.startsWith('/influencer') && !location.pathname.startsWith('/influencer/register');

  // Chargement de la détection
  if (siteMode === null) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center">
        <div className="text-[#FF6B35] text-xl">{"Chargement..."}</div>
      </div>
    );
  }

  // ============ SITE B (Paiement) — domaine dédié post-fork ============
  if (siteMode === 'site_b') {
    // Sur Site B, SEULES les routes /payment/* sont autorisées
    if (!isPaymentRoute) {
      return (
        <PaymentLayout>
          <Routes>
            <Route path="*" element={<Navigate to="/payment" replace />} />
          </Routes>
        </PaymentLayout>
      );
    }
    return (
      <PaymentLayout>
        <Routes>
          <Route path="/payment" element={<PaymentHome />} />
          <Route path="/payment/product" element={<PaymentProduct />} />
          <Route path="/payment/checkout" element={<PaymentCheckout />} />
          <Route path="/payment/success" element={<PaymentSuccessPage />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/payment/cgv" element={<PaymentCGV />} />
          <Route path="/payment/mentions-legales" element={<PaymentMentions />} />
          <Route path="/payment/confidentialite" element={<PaymentPrivacy />} />
          <Route path="*" element={<Navigate to="/payment" replace />} />
        </Routes>
      </PaymentLayout>
    );
  }

  // ============ SITE A (IPTV) — domaine principal ============
  // Sur Site A (post-fork), les routes /payment/* sont BLOQUÉES
  // En mode unifié (pré-fork), toutes les routes sont accessibles
  if (siteMode === 'site_a' && isPaymentRoute) {
    return <BlockedRoute />;
  }
  
  // En mode unifié (pré-fork), les routes /payment/* utilisent le PaymentLayout
  if (siteMode === 'unified' && isPaymentRoute) {
    return (
      <PaymentLayout>
        <Routes>
          <Route path="/payment" element={<PaymentHome />} />
          <Route path="/payment/product" element={<PaymentProduct />} />
          <Route path="/payment/checkout" element={<PaymentCheckout />} />
          <Route path="/payment/success" element={<PaymentSuccessPage />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/payment/cgv" element={<PaymentCGV />} />
          <Route path="/payment/mentions-legales" element={<PaymentMentions />} />
          <Route path="/payment/confidentialite" element={<PaymentPrivacy />} />
          <Route path="*" element={<Navigate to="/payment" replace />} />
        </Routes>
      </PaymentLayout>
    );
  }

  return (
    <MaintenanceCheck>
      <div className="flex flex-col min-h-screen bg-[#0A0A0B]">
        <Toaster theme="dark" position="top-center" richColors closeButton />
        <DynamicPageTitle />
        {!isAdminRoute && !isInfluencerRoute && !isMaintenancePage && <Navbar />}
        {!isAdminRoute && !isInfluencerRoute && !isMaintenancePage && <IncidentModal />}
        <main className={`flex-grow ${!isAdminRoute && !isInfluencerRoute && !isMaintenancePage ? 'pt-16' : ''}`}>
          <Routes>
            <Route path="/maintenance" element={<MaintenancePage />} />
            <Route path="/maintenance/exit" element={<MaintenanceExit />} />
            <Route path="/" element={<Home />} />
            <Route path="/offre" element={<Offer />} />
            <Route path="/telechargements" element={<DownloadCenter />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/tutoriels" element={<Tutorials />} />
            <Route path="/tutoriels-et-assistance" element={<Navigate to="/tutoriels" replace />} />
            <Route path="/assistance" element={<Assistance />} />
            <Route path="/devenir-vendeur" element={<BecomeVendor />} />
            <Route path="/programme-influenceur" element={<InfluencerProgram />} />
            <Route path="/mentions-legales" element={<MentionsLegales />} />
            <Route path="/cgu" element={<TermsClients />} />
            <Route path="/cgu-vendeurs" element={<TermsVendors />} />
            <Route path="/confidentialite" element={<Privacy />} />
            <Route path="/connexion" element={<Login />} />
            <Route path="/inscription" element={<Register />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/verify-email-pending" element={<VerifyEmailPending />} />
            <Route path="/mot-de-passe-oublie" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/change-password/:token" element={<ChangePassword />} />
            <Route path="/verify-email-change/:token" element={<VerifyEmailChange />} />
            <Route path="/paiement/:token" element={<PaymentLink />} />
            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/parrainage/:referralCode" element={<ReferralLanding />} />
            <Route path="/influencer/register" element={<InfluencerRegister />} />
            
            <Route path="/admin/login" element={<AdminLogin />} />
            
            <Route
              path="/client"
              element={
                <ProtectedRoute allowedRoles={['client']}>
                  <ClientDashboard />
                </ProtectedRoute>
              }
            />
            
            <Route
            path="/client/credits-history"
            element={
              <ProtectedRoute allowedRoles={['client']}>
                <ClientCreditsHistory />
              </ProtectedRoute>
            }
          />
          
          <Route path="/client/tickets" element={<Navigate to="/assistance" replace />} />
          <Route path="/client/tickets/:ticketId" element={<Navigate to="/assistance" replace />} />
          <Route path="/vendeur/tickets" element={<Navigate to="/assistance" replace />} />
          <Route path="/vendeur/tickets/:ticketId" element={<Navigate to="/assistance" replace />} />
          
          <Route
            path="/settings"
            element={
              <ProtectedRoute allowedRoles={['client', 'vendor', 'admin']}>
                <AccountSettings />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/vendor"
            element={
              <ProtectedRoute allowedRoles={['vendor']}>
                <VendorDashboard />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminLayout>
                  <AdminDashboard />
                </AdminLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/assistant"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminLayout>
                  <React.Suspense fallback={<div className="p-8 text-[#B8B8BA]">Chargement…</div>}>
                    <AssistantAdmin />
                  </React.Suspense>
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/clients"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminLayout>
                  <ClientsManagement />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/vendors"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminLayout>
                  <VendorsManagement />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/payouts"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminLayout>
                  <PayoutsManagement />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/influencers"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminLayout>
                  <AdminInfluencers />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/renewals"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminLayout>
                  <RenewalsManagement />
                </AdminLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/tickets"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminLayout>
                  <AdminTickets />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/tickets/:ticketId"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminLayout>
                  <AdminTickets />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/pending-validations"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminLayout>
                  <PendingValidations />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/analytics"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminLayout>
                  <Analytics />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/fraud"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminLayout>
                  <FraudPage />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/incident"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminLayout>
                  <IncidentModalPage />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/reviews"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminLayout>
                  <ReviewsManagement />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/emails"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminLayout>
                  <EmailsPage />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/email-logs"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminLayout>
                  <EmailLogsPage />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/settings"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminLayout>
                  <AdminSettings />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/vendor/academy"
            element={
              <ProtectedRoute allowedRoles={['vendor']}>
                <Academy />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vendor/statistics"
            element={
              <ProtectedRoute allowedRoles={['vendor']}>
                <VendorStatistics />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/vendor/analytics"
            element={
              <ProtectedRoute allowedRoles={['vendor']}>
                <VendorAnalytics />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/vendor/transactions"
            element={
              <ProtectedRoute allowedRoles={['vendor']}>
                <VendorTransactions />
              </ProtectedRoute>
            }
          />

          {/* Influencer Routes */}
          <Route
            path="/influencer"
            element={
              <ProtectedRoute allowedRoles={['influencer']}>
                <InfluencerLayout><InfluencerDashboard /></InfluencerLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/influencer/stats"
            element={
              <ProtectedRoute allowedRoles={['influencer']}>
                <InfluencerLayout><InfluencerStats /></InfluencerLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/influencer/payouts"
            element={
              <ProtectedRoute allowedRoles={['influencer']}>
                <InfluencerLayout><InfluencerPayouts /></InfluencerLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/influencer/profile"
            element={
              <ProtectedRoute allowedRoles={['influencer']}>
                <InfluencerLayout><InfluencerProfile /></InfluencerLayout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </main>
      {!isMaintenancePage && !isAdminRoute && !isInfluencerRoute && <Footer />}
    </div>
    </MaintenanceCheck>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <GTMProvider />
        <AppRoutes />
        <NovaFloatingWidget />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
